import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  learningFieldTargetKey,
  learningTableTargetKey,
  learningTableTrialLabel,
} from './learning-attempts';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { folds } from './LearningAIQVisual';
import type {
  LearningStatus,
  LearningFieldMeasurement,
  LearningTableMeasurement,
} from '../shared/desktop-api';
import {
  scoredLearningAttempts,
  exampleLearningAttempts,
  attemptGeometry,
  learningAttemptPage,
  measuredLearningAttempts,
  learningTargetSelection,
  unmeasuredLearningReason,
  historicalTypeComparisons,
  savedLearningComparisons,
  learningResultUnavailable,
  learningReplayIsCurrent,
} from './learning-attempts';
import './learning-paths.css';

export function LearningPaths({
  status,
  unavailable = false,
  live = false,
  fields = [],
  tables = [],
  history = [],
  fieldsUnavailable = false,
  tablesUnavailable = false,
  requestedTarget,
}: {
  status: LearningStatus | null;
  unavailable?: boolean;
  live?: boolean;
  fields?: LearningFieldMeasurement[];
  tables?: LearningTableMeasurement[];
  history?: LearningStatus[];
  fieldsUnavailable?: boolean;
  tablesUnavailable?: boolean;
  requestedTarget?: { key: string; request: number };
}) {
  const [preview, setPreview] = useState(!requestedTarget),
    [paused, setPaused] = useState(false),
    [replay, setReplay] = useState(0);
  const [selected, setSelected] = useState(requestedTarget?.key ?? '');
  const [attemptPage, setAttemptPage] = useState<{ target: string; page: number } | null>(null);
  const graph = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!requestedTarget) return;
    setSelected(requestedTarget.key);
    setPreview(false);
    setPaused(false);
    setAttemptPage(null);
    setReplay((value) => value + 1);
    graph.current?.scrollIntoView({ block: 'start' });
    graph.current?.focus({ preventScroll: true });
  }, [requestedTarget]);
  const targets = [
    ...fields.map((field) => ({ ...field, targetKey: learningFieldTargetKey(field) })),
    ...tables.map((table) => ({
      ...table,
      field: 'Line items',
      trialLabel: learningTableTrialLabel(table),
      targetKey: learningTableTargetKey(table),
    })),
    ...savedLearningComparisons(status, history).map((comparison) => ({
      field: comparison.target,
      documentType: 'Job',
      recordedAt: comparison.recordedAt,
      attempts: comparison.attempts,
      targetKey: comparison.targetKey,
      historical: comparison.historical,
    })),
  ];
  const fieldKey = (field: (typeof targets)[number]) => field.targetKey;
  const historical = historicalTypeComparisons(status, history);
  const historicalChoice = historical.find((entry) => entry.key === selected);
  const selection = learningTargetSelection(targets, selected);
  const { chosen } = selection,
    missing = selection.missing && !historicalChoice;
  const selectionValue = historicalChoice?.key ?? selection.value;
  const selectedUnavailable = learningResultUnavailable(
    chosen?.targetKey,
    unavailable,
    fieldsUnavailable,
    tablesUnavailable,
  );
  const allRows = preview
    ? exampleLearningAttempts
    : missing
      ? []
      : historicalChoice
        ? scoredLearningAttempts(historicalChoice.status)
        : chosen
          ? measuredLearningAttempts(fieldKey(chosen), chosen.attempts ?? [])
          : scoredLearningAttempts(
              selected === 'types-kept' && status
                ? { ...status, recent: status.lastAppliedTypeComparison ?? [], best: null }
                : status,
            );
  const pageTarget = preview
    ? 'preview'
    : chosen
      ? fieldKey(chosen)
      : `${status?.startedAt}:${selected}`;
  const window = learningAttemptPage(
    allRows,
    attemptPage?.target === pageTarget ? attemptPage.page : null,
  );
  const rows = window.rows;
  return (
    <section
      ref={graph}
      tabIndex={-1}
      className={`learning-paths ${paused || (!preview && selectedUnavailable) ? 'is-paused' : ''}`}
      aria-label="How learning improves this job"
    >
      <header>
        <div>
          <span className="eyebrow">Training</span>
          <h3>
            {preview
              ? 'How training finds a better approach'
              : missing
                ? 'Selected learning result unavailable'
                : chosen
                  ? 'trialLabel' in chosen
                    ? `Testing ${chosen.trialLabel.toLowerCase()}`
                    : `Learning to read ${chosen.field}`
                  : 'Learning to recognize document types'}
          </h3>
          {!preview && (
            <label className="learning-attempt-target">
              Learning target{' '}
              <select
                value={selectionValue}
                onChange={(event) => {
                  setSelected(event.target.value);
                  setReplay(replay + 1);
                }}
              >
                <option value="types">Document recognition</option>
                {missing && (
                  <option value={selected} disabled>
                    Selected result unavailable
                  </option>
                )}
                {(selected === 'types-kept' || !!status?.lastAppliedTypeComparison?.length) && (
                  <option value="types-kept">
                    Document recognition · last adopted improvement
                  </option>
                )}
                {historical.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    Document recognition ·{' '}
                    {entry.adopted ? 'adopted improvement · ' : 'earlier comparison · '}
                    {new Date(entry.recordedAt).toLocaleString()}
                  </option>
                ))}
                {targets.map((field) => (
                  <option key={fieldKey(field)} value={fieldKey(field)}>
                    {field.documentType} · {'trialLabel' in field ? field.trialLabel : field.field}{' '}
                    · {new Date(field.recordedAt).toLocaleString()}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="learning-path-controls">
          <button
            className="quiet-button small"
            onClick={() => {
              if (preview && !selected) setSelected(selectionValue);
              setPreview(!preview);
              setReplay(replay + 1);
              setPaused(false);
            }}
          >
            {preview ? 'Show actual results' : 'Preview the animation'}
          </button>
          {!!rows.length && (
            <>
              <button
                className="quiet-button small"
                onClick={() => {
                  setReplay(replay + 1);
                  setPaused(false);
                }}
              >
                <RotateCcw size={14} />
                Replay
              </button>
              <button
                className="quiet-button small"
                aria-pressed={paused}
                onClick={() => setPaused(!paused)}
              >
                {paused ? <Play size={14} /> : <Pause size={14} />}{' '}
                {paused ? 'Resume animation' : 'Pause animation'}
              </button>
            </>
          )}
        </div>
      </header>
      <div className="learning-rsi-caption">
        <span>
          <RotateCcw size={14} aria-hidden="true" /> Try an approach → Test accuracy → Learn →
          Repeat
        </span>
        <span>
          {preview
            ? 'Illustration only · not your job’s results'
            : selectedUnavailable
              ? 'Update unavailable · showing last received results'
              : 'Recorded learning comparisons'}
        </span>
      </div>
      {window.count > 1 && (
        <nav className="learning-attempt-pages" aria-label="Learning attempts">
          <button
            className="quiet-button small"
            disabled={window.page === 0}
            onClick={() => {
              setAttemptPage({ target: pageTarget, page: window.page - 1 });
              setReplay(replay + 1);
            }}
          >
            Earlier attempts
          </button>
          <span>
            Attempts {window.start + 1} to {window.start + rows.length} of {allRows.length}
          </span>
          <button
            className="quiet-button small"
            disabled={window.page === window.count - 1}
            onClick={() => {
              setAttemptPage({ target: pageTarget, page: window.page + 1 });
              setReplay(replay + 1);
            }}
          >
            Later attempts
          </button>
        </nav>
      )}
      <div className="learning-paths-stage">
        <div className="learning-paths-brain" aria-hidden="true">
          <svg viewBox="0 0 300 220">
            {[false, true].map((mirror) => (
              <g
                key={String(mirror)}
                transform={mirror ? 'translate(300 0) scale(-1 1)' : undefined}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              >
                {folds.map((d, i) => (
                  <path key={i} d={d} />
                ))}
              </g>
            ))}
          </svg>
        </div>
        {rows.length ? (
          <svg
            key={replay + ':' + preview}
            viewBox={`0 0 900 ${rows.length * 62 + 60}`}
            aria-hidden="true"
          >
            {[0, 25, 50, 75, 100].map((value) => (
              <g key={value}>
                <text x={60 + value * 6.3} y={18} className="learning-path-column">
                  {value}%
                </text>
                <path
                  d={`M${60 + value * 6.3} 28V${rows.length * 62 + 35}`}
                  className="learning-attempt-grid"
                />
              </g>
            ))}
            {rows.map((row, index) => {
              const { y, end, nodes, d } = attemptGeometry(row.percent, index, rows.length);
              return (
                <g
                  key={row.id}
                  className={`learning-path learning-attempt ${row.winner ? 'adopted' : 'held'}`}
                  style={
                    {
                      '--path-delay': `${index * 2.4}s`,
                      '--pulse-path': `path("${d}")`,
                      '--attempt-cycle': `${rows.length * 2.4 + 6}s`,
                    } as CSSProperties
                  }
                >
                  <path className="learning-path-line" d={d} pathLength="1" />
                  {nodes.map((node, i) => (
                    <circle
                      key={i}
                      cx={node.x}
                      cy={node.y}
                      r={i === 6 ? 5 : 2.6}
                      className="learning-path-node"
                      style={{ '--node-delay': `${index * 2.4 + i * 0.3}s` } as CSSProperties}
                    />
                  ))}
                  <circle r="4" className="learning-path-pulse" />
                  <text x={end + 13} y={y + 4} className="learning-path-label">
                    {row.declined === row.total ? 'No answers' : Math.round(row.percent) + '%'}
                  </text>
                  <text x={755} y={y + 4} className="learning-path-name">
                    Approach {row.round}
                  </text>
                  {row.winner && (
                    <text
                      x={755}
                      y={y + 20}
                      className="learning-path-label learning-approach-winner"
                    >
                      {preview ? 'Best verified approach' : 'Verified · selected'}
                    </text>
                  )}
                  {!row.winner && row.comparisonLabel && (
                    <foreignObject
                      x={755}
                      y={y + 8}
                      width={140}
                      height={44}
                      className="learning-path-outcome"
                    >
                      <div>{row.comparisonLabel}</div>
                    </foreignObject>
                  )}
                </g>
              );
            })}
          </svg>
        ) : (
          <div className="learning-paths-empty">
            {missing
              ? 'This result is not in the currently loaded history.'
              : 'No comparable attempt scores saved for this session.'}
            <small>
              {missing
                ? 'Choose another learning result or preview the animation.'
                : chosen
                  ? chosen.attempts?.length
                    ? 'The saved attempts do not form a consistent comparison. No accuracy score is shown.'
                    : `${unmeasuredLearningReason('outcome' in chosen ? chosen.outcome : undefined)} That is not a zero accuracy score.`
                  : 'Choose a saved field experiment or preview the animation.'}
            </small>
          </div>
        )}
      </div>
      <footer>
        {preview
          ? 'Each branch is another attempt to read the same field accurately.'
          : learningReplayIsCurrent(
                live,
                selectedUnavailable,
                missing,
                !!historicalChoice || selected === 'types-kept',
                chosen,
              )
            ? 'Recorded approaches · session active'
            : 'Recorded approach replay'}
        <span>
          {preview
            ? 'Learn from each test. Use the best verified approach. Repeat as you teach it.'
            : missing
              ? 'No replacement scores shown'
              : chosen
                ? `${chosen.attempts?.[0]?.scoreScope ?? 'Search examples'} · correct / tested`
                : 'Correct answers ÷ all examples in this search comparison'}
        </span>
      </footer>
      {!!rows.length && (
        <details>
          <summary>Approach results</summary>
          <ul>
            {rows.map((row) => (
              <li key={row.id}>
                Approach {row.round}: {row.right}/{row.total} correct ({row.percent.toFixed(1)}%).{' '}
                {row.declined !== null &&
                  `${row.total - row.right - row.declined} incorrect; ${row.declined} left unanswered. `}
                {row.verdict}.
              </li>
            ))}
          </ul>
          <p>
            Path bends and signal dots illustrate exploration; they are not actual model parameters.
            {preview
              ? ' The example is not stored as learning evidence.'
              : ' A higher search score alone does not authorize adoption. Safety and independent checks still decide what is kept.'}
          </p>
        </details>
      )}
    </section>
  );
}
